import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { SettingsApiError } from "../../data/datasources/SettingsApiDataSource";
import {
  CompanySettings,
  CompanySettingsValues,
} from "../../domain/entities/CompanySettings";

/** What a panel supplies to drive the shared form machinery. */
export interface SettingsFormOperations {
  load: (signal?: AbortSignal) => Promise<CompanySettings>;
  save: (
    original: CompanySettingsValues,
    edited: CompanySettingsValues
  ) => Promise<CompanySettings>;
  /** Goes into the fallback error messages, e.g. "company settings". */
  subject: string;
}

export interface UseSettingsFormReturn {
  settings: CompanySettings | null;
  /** Current form state, including unsaved edits. */
  values: CompanySettingsValues;
  loading: boolean;
  saving: boolean;
  /** Page-level failure (load failed, or save failed for a non-field reason). */
  error: string | null;
  /** Per-field validation messages from the last save attempt. */
  fieldErrors: Record<string, string>;
  /** True when the form differs from what was last loaded or saved. */
  dirty: boolean;
  /** Set after a successful save, cleared on the next edit. */
  saved: boolean;
  setField: (field: string, value: string) => void;
  save: () => Promise<void>;
  discard: () => void;
  reload: () => Promise<void>;
  /**
   * ZOUR-19 — adopt a settings payload the panel obtained some other way.
   *
   * A panel action that is not a form save can still return the whole settings
   * payload (the GST panel's "Use Zoho's GSTIN" does). Feeding it through the
   * same path a save uses keeps `settings` and `values` in step, instead of the
   * panel holding a second, staler copy of the truth.
   */
  applyServerSettings: (result: CompanySettings) => void;
}

/**
 * Loading, editing and saving for any server-driven settings panel.
 *
 * Extracted from useCompanySettings when the GST panel arrived and needed the
 * same 140 lines with two different use cases behind them. The old hook is now
 * a thin wrapper over this, so nothing that already worked changed shape.
 *
 * Edits live in `values`, separate from the loaded `settings`, so Discard is a
 * reset rather than a refetch and the dirty check is a plain comparison. A save
 * replaces both from the server's response, because the server is what decides
 * what was actually stored — which also means a write-only field correctly
 * comes back blank and ready for the next replacement.
 *
 * `operations` must be a stable reference; panels define theirs at module scope.
 */
export const useSettingsForm = (operations: SettingsFormOperations): UseSettingsFormReturn => {
  const [settings, setSettings] = useState<CompanySettings | null>(null);
  const [values, setValues] = useState<CompanySettingsValues>({});
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [saved, setSaved] = useState<boolean>(false);

  const abortRef = useRef<AbortController | null>(null);
  const requestIdRef = useRef(0);

  const accept = useCallback((result: CompanySettings) => {
    setSettings(result);
    setValues({ ...result.values });
    setFieldErrors({});
  }, []);

  const load = useCallback(async (): Promise<void> => {
    abortRef.current?.abort();

    const controller = new AbortController();
    abortRef.current = controller;

    const requestId = ++requestIdRef.current;

    setLoading(true);
    setError(null);
    setSaved(false);

    try {
      const result = await operations.load(controller.signal);

      if (requestId === requestIdRef.current) {
        accept(result);
      }
    } catch (err: any) {
      if (controller.signal.aborted) {
        return;
      }

      if (requestId === requestIdRef.current) {
        setError(err?.message || `Failed to load ${operations.subject}`);
        // eslint-disable-next-line no-console
        console.error(`Failed to load ${operations.subject}:`, err);
      }
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  }, [accept, operations]);

  useEffect(() => {
    load();

    return () => {
      abortRef.current?.abort();
    };
  }, [load]);

  const setField = useCallback((field: string, value: string) => {
    setSaved(false);
    setValues((current) => ({ ...current, [field]: value }));
    // Clearing this field's error as it is retyped keeps the message tied to
    // the value that actually failed.
    setFieldErrors((current) => {
      if (!(field in current)) {
        return current;
      }

      const next = { ...current };
      delete next[field];

      return next;
    });
  }, []);

  const dirty = useMemo(() => {
    if (!settings) {
      return false;
    }

    return Object.keys(values).some((field) => values[field] !== settings.values[field]);
  }, [settings, values]);

  const discard = useCallback(() => {
    if (!settings) {
      return;
    }

    setValues({ ...settings.values });
    setFieldErrors({});
    setError(null);
    setSaved(false);
  }, [settings]);

  const save = useCallback(async (): Promise<void> => {
    if (!settings) {
      return;
    }

    setSaving(true);
    setError(null);
    setFieldErrors({});

    try {
      const result = await operations.save(settings.values, values);

      accept(result);
      setSaved(true);
    } catch (err: any) {
      if (err instanceof SettingsApiError && Object.keys(err.fieldErrors).length > 0) {
        setFieldErrors(err.fieldErrors);
        setError("Some fields need attention.");
      } else {
        setError(err?.message || `Failed to save ${operations.subject}`);
      }

      // eslint-disable-next-line no-console
      console.error(`Failed to save ${operations.subject}:`, err);
    } finally {
      setSaving(false);
    }
  }, [accept, operations, settings, values]);

  return {
    settings,
    values,
    loading,
    saving,
    error,
    fieldErrors,
    dirty,
    saved,
    setField,
    save,
    discard,
    reload: load,
    applyServerSettings: accept,
  };
};
